import { useCallback, useState } from "react";

import type {
  Section12PaymentRequest,
  Section12PaymentRequestWithDetails,
  UUID,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { renderErrorToast } from "~/components/design-system/index.js";

/**
 * Shared hook for opening the payment-request edit dialog from any list view.
 */
export function useEditPaymentRequest() {
  const [editingRequest, setEditingRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<UUID | null>(null);

  const startEdit = useCallback(
    async (request: Pick<Section12PaymentRequest, "id">) => {
      // Clear any previously loaded request first. The dialog seeds its form
      // state from `editingRequest` via useState initialisers, which only run
      // on mount — without this reset, reopening the same row after a save
      // keeps the dialog mounted and shows the stale pre-save values.
      setEditingRequest(null);
      setLoadingEditId(request.id);
      try {
        const response = await api.section12.getPaymentRequestById(request.id);
        setEditingRequest(response.data);
      } catch (error) {
        console.error("Failed to load payment request for editing:", error);
        renderErrorToast({
          message: "Unable to open payment request for editing",
        });
      } finally {
        setLoadingEditId(null);
      }
    },
    [],
  );

  const stopEdit = useCallback(() => {
    setEditingRequest(null);
  }, []);

  return { editingRequest, loadingEditId, startEdit, stopEdit };
}
